import { useState, useEffect, useMemo, useRef } from 'react'
import { Container } from 'react-bootstrap';
import { ArrowRightCircle } from 'react-bootstrap-icons';
import 'animate.css';
import { useTranslation } from 'react-i18next';
import useScrollReveal from '../hooks/useScrollReveal';
import RunJSFrame from './RunJSFrame';
import GlitchSignature from './GlitchSignature';
import { signaturePaths, signatureViewBox } from '../data/signaturePaths';

function useParallax(speed = 0.15) {
    const ref = useRef(null);
    const [offset, setOffset] = useState(0);

    useEffect(() => {
        const handleScroll = () => {
            if (ref.current) {
                const rect = ref.current.getBoundingClientRect();
                const windowHeight = window.innerHeight;
                const scrollProgress = (windowHeight - rect.top) / (windowHeight + rect.height);
                setOffset((scrollProgress - 0.5) * speed * 100);
            }
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, [speed]);

    return [ref, offset];
}

function Reveal({ children, type = 'reveal-fade', delay = 0, className = '' }) {
    const ref = useScrollReveal();
    const delayClass = delay ? `reveal-delay-${delay}` : '';
    return (
        <div ref={ref} className={`${type} ${delayClass} ${className}`}>
            {children}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// Nombre anterior con efecto scramble — OCULTO a pedido del usuario.
// El código se conserva acá para poder restaurarlo: descomentar el bloque
// y volver a usar <ScrambleText text="Facundo N. Corona Martínez" />
// en el banner-hero-block (de abajo).
// ─────────────────────────────────────────────────────────────
/*
const SCRAMBLE_CHARSET =
    'アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン' +
    '日月火水木金土ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%^&*()[]{}<>/\\|';

function ScrambleText({ text, scrambleSpeed = 40 }) {
    const chars = useMemo(() => text.split(''), [text]);
    const [started, setStarted] = useState(false);
    const [allResolved, setAllResolved] = useState(false);
    const [displayChars, setDisplayChars] = useState(() =>
        chars.map(c => c === ' ' ? ' ' : SCRAMBLE_CHARSET[Math.floor(Math.random() * SCRAMBLE_CHARSET.length)])
    );
    const resolvedRef = useRef(new Set());
    const fallenRef = useRef(new Set());
    const [fallenChars, setFallenChars] = useState(new Set());

    // Completely random fall delays per character (not sequential)
    const fallDelays = useMemo(() => {
        const d = {};
        for (let i = 0; i < chars.length; i++) {
            if (chars[i] !== ' ') d[i] = 200 + Math.random() * 2200;
        }
        return d;
    }, [chars]);

    // Random fall durations
    const fallDurations = useMemo(() => {
        const d = {};
        for (let i = 0; i < chars.length; i++) {
            if (chars[i] !== ' ') d[i] = 500 + Math.random() * 400;
        }
        return d;
    }, [chars]);

    // Random glitch animation delays so letters glitch uncoordinated
    const glitchDelays = useMemo(() => {
        const d = {};
        for (let i = 0; i < chars.length; i++) {
            if (chars[i] !== ' ') d[i] = -(Math.random() * 9); // -0s to -9s
        }
        return d;
    }, [chars]);

    // Start after short delay
    useEffect(() => {
        const timer = setTimeout(() => setStarted(true), 300);
        return () => clearTimeout(timer);
    }, []);

    // Scramble unresolved characters
    useEffect(() => {
        if (!started) return;
        let active = true;
        const interval = setInterval(() => {
            if (!active) return;
            setDisplayChars(prev => {
                const next = [...prev];
                const resolved = resolvedRef.current;
                for (let i = 0; i < chars.length; i++) {
                    if (chars[i] === ' ' || resolved.has(i)) continue;
                    const r = SCRAMBLE_CHARSET[Math.floor(Math.random() * SCRAMBLE_CHARSET.length)];
                    next[i] = r === chars[i] ? chars[i] : r;
                }
                return next;
            });
        }, scrambleSpeed);
        return () => { active = false; clearInterval(interval); };
    }, [started, chars, scrambleSpeed]);

    // Trigger falls at random delays, then resolve after fall completes
    useEffect(() => {
        if (!started) return;
        let active = true;
        const timers = [];
        const nonSpaceIndices = chars
            .map((c, i) => (c !== ' ' ? i : -1))
            .filter(i => i !== -1);

        let maxResolveTime = 0;

        nonSpaceIndices.forEach(charIdx => {
            const fDelay = fallDelays[charIdx];
            const fDuration = fallDurations[charIdx];

            // Trigger the fall (char becomes visible + animates down)
            timers.push(setTimeout(() => {
                if (!active) return;
                fallenRef.current.add(charIdx);
                setFallenChars(prev => new Set(prev).add(charIdx));
            }, fDelay));

            // Resolve the char after fall animation completes
            const resolveAt = fDelay + fDuration + 100;
            timers.push(setTimeout(() => {
                if (!active) return;
                resolvedRef.current.add(charIdx);
                setDisplayChars(prev => { const n = [...prev]; n[charIdx] = chars[charIdx]; return n; });
            }, resolveAt));

            maxResolveTime = Math.max(maxResolveTime, resolveAt);
        });

        // Mark all resolved after the last char settles
        timers.push(setTimeout(() => {
            if (!active) return;
            setDisplayChars([...chars]);
            setAllResolved(true);
        }, maxResolveTime + 300));

        return () => { active = false; timers.forEach(clearTimeout); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [started]);

    const renderChars = () => {
        const spans = displayChars.map((char, i) => {
            const isSpace = chars[i] === ' ';
            const resolved = resolvedRef.current.has(i) || isSpace;
            const fallen = fallenChars.has(i) || isSpace;
            const dur = fallDurations[i] || 600;

            if (isSpace) return null;

            const charStyle = {
                display: 'inline-block',
            };

            if (!fallen) {
                // Not yet fallen — invisible, positioned above
                charStyle.opacity = 0;
                charStyle.transform = 'translateY(-120px)';
            } else if (!resolved) {
                // Falling — animate with trail
                charStyle.animation = `charFall ${dur}ms cubic-bezier(0.34, 1.56, 0.64, 1) forwards`;
                charStyle.color = '#07FAEE';
            } else {
                // Resolved — final position
                charStyle.transition = 'color 0.3s, text-shadow 0.3s';
                charStyle.color = 'var(--text-primary)';
                charStyle.textShadow = 'none';
                charStyle.opacity = 1;
                charStyle.transform = 'translateY(0)';
                
                if (allResolved) {
                    charStyle.animationDelay = `${glitchDelays[i]}s`;
                }
            }

            return (
                <span key={i} style={{ display: 'inline-block', position: 'relative' }}>
                    <span style={{ visibility: 'hidden' }}>{chars[i]}</span>
                    <span style={{
                        position: 'absolute',
                        left: '50%',
                        top: 0,
                        transform: 'translateX(-50%)',
                        display: 'inline-block',
                        whiteSpace: 'nowrap'
                    }}>
                        <span className="glitch-char" style={charStyle}>{char}</span>
                    </span>
                </span>
            );
        });

        const groups = [];
        for (let i = 0; i < chars.length; i++) {
            if (chars[i] === ' ') {
                if (groups.length === 0 || groups[groups.length - 1].type === 'text') {
                    groups.push({ type: 'space', content: ' ', key: i });
                } else {
                    groups[groups.length - 1].content += ' ';
                }
            } else {
                if (groups.length === 0 || groups[groups.length - 1].type === 'space') {
                    groups.push({ type: 'text', content: [], key: i });
                }
                groups[groups.length - 1].content.push(spans[i]);
            }
        }
        return groups.map((g) =>
            g.type === 'space' ? (
                <span key={g.key} style={{ whiteSpace: 'pre' }}>{g.content}</span>
            ) : (
                <span key={g.key} style={{ display: 'inline-block', whiteSpace: 'nowrap' }}>{g.content}</span>
            )
        );
    };

    return <h1 className={`scramble-text${allResolved ? ' glitch-active' : ''}`}>{renderChars()}</h1>;
}

*/
function Banner() {
    const { t } = useTranslation();
    const toRotate = useMemo(() => [t('banner.rotate1'), t('banner.rotate2'), t('banner.rotate3'), t('banner.rotate4'), t('banner.rotate5'), t('banner.rotate6'), t('banner.rotate7'), t('banner.rotate8'), t('banner.rotate9'), t('banner.rotate10'), t('banner.rotate11'), t('banner.rotate12')], [t]);
    const [loopNum, setLoopNum] = useState(0);
    const [isDeleting, setIsDeleting] = useState(false);
    const [text, setText] = useState('');
    const [delta, setDelta] = useState(30 + Math.random() * 50);
    const [flashTrigger, setFlashTrigger] = useState(0);
    const period = 1500;
    const [parallaxRef] = useParallax(0.12);

    useEffect(() => {
        const tick = () => {
            const i = loopNum % toRotate.length;
            const fullText = toRotate[i];
            if (isDeleting) {
                setText(fullText.substring(0, text.length - 1));
            } else {
                setText(fullText.substring(0, text.length + 1));
            }
            if (isDeleting) {
                setDelta(prev => 20 + Math.random() * 30);
            }
            if (!isDeleting && text === fullText) {
                setFlashTrigger(prev => prev + 1);
                setDelta(period);
                setIsDeleting(true);
            } else if (isDeleting && text === '') {
                setIsDeleting(false);
                setLoopNum(loopNum + 1);
                setDelta(30 + Math.random() * 50);
            } else {
                setDelta(30 + Math.random() * 50);
            }
        };
        const timer = setTimeout(tick, delta);
        return () => clearTimeout(timer);
    });

    useEffect(() => {
        const wrap = document.querySelector('.banner-typewriter > .wrap');
        if (wrap && flashTrigger > 0) {
            // En modo claro el resplandor cian no debe verse: el estilo inline
            // pisa cualquier regla CSS, asi que hay que dejarlo en 'none'.
            const isLight = document.body.classList.contains('light-theme');
            if (isLight) {
                wrap.style.textShadow = 'none';
                return undefined;
            }
            const flashShadow = '0 0 20px rgba(7,250,238,0.9), 0 0 40px rgba(7,250,238,0.5)';
            wrap.style.textShadow = flashShadow;
            const timeout = setTimeout(() => {
                // se restaura el valor de CSS (no un inline fijo)
                wrap.style.textShadow = '';
            }, 200);
            return () => clearTimeout(timeout);
        }
    }, [flashTrigger]);

    // Sincroniza el resplandor inline con el tema (al montar o al cambiar de tema)
    useEffect(() => {
        const wrap = document.querySelector('.banner-typewriter > .wrap');
        if (!wrap) return undefined;
        const apply = () => {
            wrap.style.textShadow = document.body.classList.contains('light-theme') ? 'none' : '';
        };
        apply();
        if (!window.MutationObserver) return undefined;
        const observer = new MutationObserver(apply);
        observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        return () => observer.disconnect();
    }, []);

    return (
        <div className="fondo" ref={parallaxRef}>
            <section className="banner" id="home" style={{ position: 'relative' }}>
                <Container>
                    {/* ── Bloque del nombre anterior (oculto) ─────────────────────────────
                        Para restaurarlo: descomentar este bloque y volver a usar
                        <ScrambleText text="Facundo N. Corona Martínez" /> (el componente esta
                        comentado mas arriba en este archivo).
                        <Reveal type="reveal-scale" delay={1} className="text-center">
                            <div className="banner-hero-block">
                                <div className="banner-hero-glow" />
                                <div className="banner-line-left" />
                                <div className="banner-line-right" />
                                <ScrambleText text="Facundo N. Corona Martínez" />
                                <h1 className="visually-hidden">Facundo N. Corona Martínez</h1>
                            </div>
                        </Reveal>
                        ────────────────────────────────────────────────────────────────────── */}
                    <Reveal type="reveal-fade" delay={2} className="text-center">
                        <div className="banner-signature">
                            <div className="banner-hero-glow" />
                            <h1 className="visually-hidden">Facundo N. Corona Martínez</h1>
                            <GlitchSignature
                                paths={signaturePaths}
                                viewBoxWidth={signatureViewBox.width}
                                viewBoxHeight={signatureViewBox.height}
                                strokeColor="currentColor"
                                fillColor="currentColor"
                                fillEnabled={true}
                                strokeWidth={2.6}
                                intensity={6}
                                sliceCount={6}
                                colorGlitchOne="#ff0044"
                                colorGlitchTwo="#00ff9d"
                                colorGlitchThree="#0088ff"
                                drawProps={{
                                    duration: 1.6,
                                    delay: 0.2,
                                    stagger: 0.04,
                                    easing: 'easeInOut',
                                    direction: 'forward',
                                    triggerPosition: 0,
                                    triggerAmount: 0,
                                    replay: true
                                }}
                            />
                        </div>
                    </Reveal>
                    <Reveal type="reveal-fade" delay={3} className="text-center">
                        <h3 className="banner-typewriter"><span className="wrap" style={{ fontSize: '1.5rem' }}>{text}</span></h3>
                    </Reveal>

                    <Reveal type="reveal-fade" delay={4} className="text-center">
                        <div className="banner-description">
                            <p>{t('banner.p1')}</p>
                            <p>{t('banner.p2')}</p>
                        </div>
                    </Reveal>

                    <Reveal type="reveal-fade" delay={5}>
                        <RunJSFrame />
                    </Reveal>

                    <div className="text-center banner-btn-centered">
                        <Reveal type="reveal-scale" delay={6}>
                            <a href="#newsletter">
                                <button className="tagline" onClick={() => console.log('connect')}>{t('banner.subscribe')} <ArrowRightCircle size={25} /></button>
                            </a>
                        </Reveal>
                    </div>
                </Container>
            </section>
        </div>
    )
}

export default Banner
